<template>
  <div>

    <ProgressSpinner 
      v-if="loadingData"
      style="width:25px; height:25px; width: 100%;" 
      strokeWidth="5" 
      fill="#EEEEEE" 
      animationDuration=".5s"
    />
    <div v-else class="card card-w-title global-sales p-fluid">

      <!-- The title opens the report behind the list, pre-filtered (item 4, decision 2a); a row
           still opens its own record, which is what it has always done. -->
      <h5 v-if="drillable" class="dashboard-drillable" @click="openReport">
        {{ title }}
      </h5>
      <h5 v-else>{{ title }}</h5>
      <div v-if="datesNote" class="dashboard-dates-note">Dates do not apply</div>
      <!-- P7: the Tasks tile shows tasks, events or both (remembered in this browser) -->
      <SelectButton v-if="isTasksTile" id="tasksTileKind" class="tasks-tile-kind" v-model="tileKind"
        :options="tileKinds" optionLabel="label" optionValue="value" @input="tileKindChanged" />

      <DataTable :value="data" class="p-datatable-responsive" :selection.sync="selectedRows" selectionMode="single" dataKey="position" style="margin-bottom: 20px">
        <Column
          v-for="(item, index) in colFields"
          :key="index"
          :field="item"
          :header="colHeaders[index]"
          :headerStyle="'width: ' + colSize[index] + 'px;'"
        >
          <template #body="slotProps">
            <div class="dashboard-row-cell">
              <div
                @click="call(slotProps.data, colFields[index])"
                :class="getStyle(slotProps.data, colFields[index])"
              >
                {{ getToShow(slotProps.data[item], colType[index]) }}
              </div>

              <!-- W7 T5: the name cell keeps opening the customer record, so the report
                   drill is a second action on the row rather than a rival for the click. -->
              <Button
                v-if="showRowDrill(slotProps.data, colFields[index])"
                icon="pi pi-chart-bar"
                class="p-button-text p-button-sm dashboard-row-drill"
                title="Sales for this customer"
                @click.stop="openRowReport(slotProps.data)"
              />
            </div>
          </template>
        </Column>
      </DataTable>
    </div>

  </div>
</template>

<script>
import UserService from "@/service/UserService";
import { barAppliesTo, barDimensionParams, hasDate, withQuery } from './dashboardFilterBar'
import { drillTarget, hasDrill, rowDrillTarget } from './dashboardDrill'
import { formatWidgetValue } from './widgetValue'
import { TASKS_TILE_KINDS, readTasksTileKind, saveTasksTileKind, isTasksTileEndpoint, tasksTileQuery } from './tasksTileKind'

export default {
  created() {
    console.dir("Table widget is created.")

    this.setupComponent()
  },
  props: {
    widget: {
      type: Object,
      required: false
    },
    // The dashboard filter bar; its four dimensions go to the endpoint (dates not yet).
    filterBar: {
      type: Object,
      default: null
    },
    // What this widget's title opens: { reportId, drill, rowSearchField }, built by the backend
    // from the filters the widget's own figures ran with (v1/getUserWidgetDrills).
    drill: {
      type: Object,
      default: null
    }
  },
  data: () => ({
    tileKinds: TASKS_TILE_KINDS,
    tileKind: readTasksTileKind(),
    userService: new UserService(),
    colFields: [],
    colHeaders: [],
    colType: [],
    colSize: [],
    loadingData: false,
    data: [],
    selectedRows: null,
    userId: null
  }),
  computed: {
    // P7: the Tasks tile has a Tasks / Events / Both switch
    isTasksTile() {
      return !!(this.widget && this.widget.widget && isTasksTileEndpoint(this.widget.widget.widgetEP))
    },
    title() {
      return this.widget && this.widget.widget && this.widget.widget.widgetDescription
        ? this.widget.widget.widgetDescription
        : 'TOP 10 QUOTES'
    },
    reports() {
      return (this.$store && this.$store.getters['reports/getAllReports']) || []
    },
    drillable() {
      return hasDrill(this.drill, this.reports)
    },
    datesNote() {
      return !!(this.widget && this.widget.widget && barAppliesTo(this.widget.widget.widgetEP) && hasDate(this.filterBar))
    },
    profile() {
      return this.$store.getters['auth/getMyProfile']
    }
  },
  watch: {
    filterBar(val, old) {
      if (this.widget && this.widget.widget && barAppliesTo(this.widget.widget.widgetEP) &&
        JSON.stringify(barDimensionParams(val)) !== JSON.stringify(barDimensionParams(old))) {
        this.loadData()
      }
    },
    profile(val) {
      if (val && val.id) {
        this.setupcomponent()
      }
    }
  },
  methods: {
    tileKindChanged(kind) {
      saveTasksTileKind(kind)
      this.loadData()
    },
    openReport() {
      const target = drillTarget(this.drill, this.reports)
      if (target) this.$router.push(target)
    },
    // W7 T5: is there a report drill for this cell? `rowSearchField` names the field on the
    // widget's own row, so the icon only shows where that field is and the row has a value.
    showRowDrill(data, col) {
      return !!(this.drillable && this.drill && this.drill.rowSearchField === col && data && data[col])
    },
    openRowReport(data) {
      const target = rowDrillTarget(this.drill, data, this.reports)
      if (target) this.$router.push(target)
    },
    call(data, col) {
      // The record links come first: the customer name opens the customer record, as it always
      // has. The row drill is only the whole-cell click on a column that opens no record
      // (decision 2a) - otherwise it would take the customer link away (W7 T5).
      if (col === 'customerName' && data.customerId) {
        this.$router.push({
          path: "/customers/" + data.customerId + "/detail",
          params: { custid: data.customerId },
        });

      } else if (col === 'quoteNo' && data.oppId) {
        this.$router.push({
          path: "/enquiries/" + data.oppId + "/detail",
          params: { oppId: data.oppId },
        });
        
      } else if (col === 'subject' && data.activityId) {
        this.$router.push({
          path: "/activities/" + data.activityId + "/detail",
          params: { activityId: data.activityId },
        });

      } else if (this.showRowDrill(data, col)) {
        this.openRowReport(data)
      }
    },
    getStyle(data, col) {
      
      if (
        col === 'customerName' ||
        col === 'quoteNo' ||
        col === 'subject' ||
        (this.drill && this.drill.rowSearchField === col && this.drillable)
      ) {
        return 'under_lined'
      }
    },
    setupComponent() {
      if (this.$route.params.userid) {
        this.userId = this.$route.params.userid
      } else if (this.profile && this.profile.id) {
        this.userId = this.$store.getters['auth/getMyProfile'].id
      }
      

      if (this.widget && this.$store.getters['auth/getMyProfile']) {
        if (this.widget.widget) {

          this.colFields = [], this.colHeaders = []

          if (this.widget.widget.listColumns) {
            this.colFields = this.widget.widget.listColumns.split(',')
            this.colFields = this.colFields.map(a => a.trim())
          }

          if (this.widget.widget.listColHeaders) {
            let a = this.widget.widget.listColHeaders.split(',')
            a.forEach(b => {
              if (b) {
                let c = b.trim(), 
                  index = c.indexOf('['), 
                  index1 = c.indexOf(']')
                

                this.colHeaders.push(c.substr(0, index))
                this.colType.push(c.substr(index + 1, index1 - index - 1))
                this.colSize.push(c.substr(index1 + 2, c.length - index1 - 3))
              }
            })
          }

          if (this.colHeaders.length < this.colFields.length) {
            while(this.colHeaders.length < this.colFields.length) {
              this.colHeaders.push('')
              this.colType.push('String')
              this.colSize.push('60')
            }
          }

          

          this.loadData()
        }
      }
    },
    loadData() {
      if (this.widget && this.widget.widget && this.widget.widget.widgetEP && this.$store.getters['auth/getMyProfile']) {
        this.loadingData = true

        let path = this.widget.widget.widgetEP.replace('{userId}', this.userId)
        if (barAppliesTo(this.widget.widget.widgetEP)) {
          path = withQuery(path, barDimensionParams(this.filterBar))
        }
        if (this.isTasksTile) {
          path = withQuery(path, tasksTileQuery(this.tileKind))
        }

        this.userService
          .getCustomUserWidgetData(path)
          .then(data => {
            this.loadingData = false
            this.data = data
          })
          .catch(error => {
            this.loadingData = false
            
            this.$store.dispatch('addErrorMessage', {
              severity: 'error',
              content: 'Some data was not loaded from "' + path + '"'
            })
          })
      }
    },
    // W7 tail: one formatter for the tile and the list widget, so the tile can never again be
    // the copy that is missing a valueType.
    getToShow: formatWidgetValue
  }
}
</script>

<style>
.tasks-tile-kind { margin-bottom: 8px; }
.tasks-tile-kind .p-button { padding: 2px 10px; }
/* W7 T5: the name and its report action sit on one line; the icon must not push the
   name around or reserve space on a row that has no drill. */
.dashboard-row-cell {
  display: flex;
  align-items: center;
}

.dashboard-row-cell .dashboard-row-drill {
  width: 1.5rem;
  height: 1.5rem;
  padding: 0;
  margin-left: 0.25rem;
  flex: 0 0 auto;
}
</style>